import { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import { IitensMovimentoDialog, VendasService } from '../../../shared/services/api/estoque/venda/venda';

// Peças da listagem de vendas que mostram o que antes só aparecia abrindo a
// venda: a nota fiscal emitida (tipo, número e situação) e os itens.

const formatarMoeda = (valor: number) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatarQtd = (valor: number) => Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

// ---------- Nota fiscal ----------

interface INotaFiscalVendaProps {
  tipo?: string | null; // 'NFC-e' | 'NF-e'
  numero?: number | null;
  status?: number | null;
  compacto?: boolean;
}

// Situação da nota (tabelas nfe/nfce): 2 = autorizada, 3 = cancelada,
// 4 = inutilizada, 5 = denegada; qualquer outro valor = ainda não autorizada
// (pendente/rejeitada — ver o histórico de emissão na tela da nota).
const situacaoNota = (status?: number | null) => {
  if (status === 2) return { rotulo: 'autorizada', cor: 'success' as const };
  if (status === 3) return { rotulo: 'cancelada', cor: 'error' as const };
  if (status === 4) return { rotulo: 'inutilizada', cor: 'default' as const };
  if (status === 5) return { rotulo: 'denegada', cor: 'error' as const };
  return { rotulo: 'não autorizada', cor: 'warning' as const };
};

export const NotaFiscalVenda: React.FC<INotaFiscalVendaProps> = ({ tipo, numero, status, compacto }) => {
  // undefined = backend ainda sem essa informação (versão antiga) — não
  // mostra nada, pra não dizer "Sem nota" de uma venda que tem nota.
  if (tipo === undefined) return null;
  if (!tipo) {
    return <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>{compacto ? '' : 'Sem nota'}</Typography>;
  }
  const situacao = situacaoNota(status);
  return (
    <Tooltip title={`${tipo === 'NFC-e' ? 'Cupom fiscal (NFC-e, modelo 65)' : 'Nota fiscal (NF-e, modelo 55)'} nº ${numero ?? '-'} — ${situacao.rotulo}`}>
      <Chip
        icon={<ReceiptLongRoundedIcon sx={{ fontSize: '0.85rem !important' }} />}
        label={`${tipo} ${numero ?? ''}${status === 2 ? '' : ` · ${situacao.rotulo}`}`}
        size="small"
        color={situacao.cor}
        variant={status === 2 ? 'filled' : 'outlined'}
        sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700, maxWidth: '100%', '& .MuiChip-label': { px: 0.75 } }}
      />
    </Tooltip>
  );
};

// ---------- Itens da venda ----------

// Itens já carregados nesta sessão — reabrir a mesma venda não busca de novo.
const cacheItens = new Map<string, IitensMovimentoDialog[]>();

interface IItensDaVendaProps {
  idmov: string;
  // Celular: lista empilhada em vez de mini-tabela.
  compacto?: boolean;
}

export const ItensDaVenda: React.FC<IItensDaVendaProps> = ({ idmov, compacto }) => {
  const theme = useTheme();
  const [itens, setItens] = useState<IitensMovimentoDialog[] | null>(cacheItens.get(idmov) ?? null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (cacheItens.has(idmov)) return;
    let cancelado = false;
    VendasService.getById(idmov).then((r) => {
      if (cancelado) return;
      if (r instanceof Error) { setErro(r.message); return; }
      const lista = r.itens || [];
      cacheItens.set(idmov, lista);
      setItens(lista);
    });
    return () => { cancelado = true; };
  }, [idmov]);

  if (erro) return <Typography variant="caption" color="error.dark">{erro}</Typography>;
  if (!itens) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Carregando itens...</Typography>
      </Box>
    );
  }
  if (!itens.length) return <Typography variant="caption" color="text.secondary">Venda sem itens.</Typography>;

  const totalItens = itens.reduce((s, i) => s + Number(i.total || 0), 0);

  if (compacto) {
    return (
      <Box display="flex" flexDirection="column" gap={0.75}>
        {itens.map((i) => (
          <Box key={i.iditens} display="flex" gap={1} alignItems="flex-start">
            <Box flex={1} minWidth={0}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, lineHeight: 1.3 }}>{i.descricao}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatarQtd(i.qtds)} {i.un} × {formatarMoeda(i.preco)}{Number(i.desconto) > 0 ? ` · desc. ${formatarMoeda(i.desconto)}` : ''}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatarMoeda(i.total)}</Typography>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" textAlign="right">
          {itens.length} {itens.length === 1 ? 'item' : 'itens'} · {formatarMoeda(totalItens)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 1.5, border: `1px solid ${theme.torq.border.default}`, bgcolor: alpha(theme.palette.background.paper, 0.6), overflow: 'hidden' }}>
      <Table size="small" sx={{ '& td, & th': { fontSize: '0.75rem', py: 0.5, px: 1.25, borderBottom: `1px solid ${theme.torq.border.default}` } }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', bgcolor: theme.torq.surface.level2 } }}>
            <TableCell>Produto</TableCell>
            <TableCell align="right" sx={{ width: 90 }}>Qtd</TableCell>
            <TableCell align="center" sx={{ width: 50 }}>Un</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Preço</TableCell>
            <TableCell align="right" sx={{ width: 100 }}>Desconto</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {itens.map((i) => (
            <TableRow key={i.iditens}>
              <TableCell>
                <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{i.descricao}</Typography>
                {(i.referencia || i.marca) && (
                  <Typography noWrap sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                    {[i.referencia ? `Ref ${i.referencia}` : null, i.marca].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{formatarQtd(i.qtds)}</TableCell>
              <TableCell align="center">{i.un}</TableCell>
              <TableCell align="right">{formatarMoeda(i.preco)}</TableCell>
              <TableCell align="right">{Number(i.desconto) > 0 ? formatarMoeda(i.desconto) : '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatarMoeda(i.total)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={5} sx={{ fontWeight: 700, borderBottom: 0 }}>
              {itens.length} {itens.length === 1 ? 'item' : 'itens'}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 800, borderBottom: 0 }}>{formatarMoeda(totalItens)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
